import { FormattedMessage, IntlShape, injectIntl, useIntl } from 'react-intl';
import { SortOrder } from '../../../api/shared.dto';
import { PagingViewModel } from '../../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import React from 'react';
import DefaultButton from '../../global/default-button/default-button';
import Text from '../../global/text/text';
import { ResourcesNavigation } from '../../_events/_manage-event/shared/navigation/navigation';
import { Paging } from '../../global/_paging/paging-container';
import { NoDataFound } from '../../global/no-data';
import { ITheadSort, ListLayout } from '../../global/tables/table-list-layout';
import { TTelephoneNumberSetsRequestSortKeys } from '../../../api/zoom.dao';
import { connectZoomVM } from '../zoom.context';
import { SearchInput } from '../../global/search-input/search-input';
import { TelephoneNumberSetsViewModel } from './telephone-number-sets.viewmodel';
import { hasPermission } from '../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../api/roles.dao';
import { EDIT_NUMBER_SET_TEXT } from '../../../constants/telephone-number-set-form.text';
import { AudioBridgeProviderLabel } from '../../../infrastructure/utils/common.utils';

interface ITelephoneNumeberSetProps {
    pagingVM: PagingViewModel;
    telephoneNumberSets: ITelephoneNumeberSets[];
    telephoneNumberSetsLength: number;
    pageNumber: number;
    pageSize: number;
    sortRows: (sortField: string, sortOrder: SortOrder) => void;
    sortOrder: SortOrder;
    sortField: string;
    searchQuery: string;
    setSearchQuery: (query: string, needExecuteSearch?: boolean) => void;
    executeSearch: () => void;
    fetchError: string;
}

interface ITelephoneNumberSetProps {
    telephoneNumberList: ITelephoneNumeberSetProps;
    vm: TelephoneNumberSetsViewModel;
    intl?: IntlShape;
}

interface ITelephoneNumeberSets {
    name: string;
    provider: string;
    telephoneNumbersCount?: number;
    id?: string;
}
interface IProps extends ITelephoneNumeberSets {
    isSelected?: boolean;
    editNumberSet?: (title: string, id: string) => void;
}

const TelephoneNumberSet = (props: IProps) => {
    const intl = useIntl();
    const providerName = AudioBridgeProviderLabel[props.provider];
    const onEditClick = (): void => {
        props.editNumberSet(intl.formatMessage(EDIT_NUMBER_SET_TEXT), props.id);
    };
    const numberSetsListPermission = hasPermission(Permission.TELEPHONE_NUMBER_SETS);

    return (
        <tr className="tracked-user-row" onDoubleClick={numberSetsListPermission ? onEditClick : undefined}>
            <Text
                className="hoverable"
                tag="td"
                message={props.name}
                withEllipsis
            />
            <Text
                className="hoverable"
                tag="td"
                message={providerName}
                withEllipsis
            />
            <td className="hoverable">{props.telephoneNumbersCount}</td>
            <td className='hoverable align-right'>
                {numberSetsListPermission &&
                    <DefaultButton
                        className='visible-while-hovered'
                        text={<FormattedMessage {...EDIT_NUMBER_SET_TEXT} />}
                        modifier={['fat', 'confirm-safe']}
                        onClick={() => props.editNumberSet(intl.formatMessage(EDIT_NUMBER_SET_TEXT), props.id)}
                    />
                }
            </td>
        </tr>
    );
};

const TelephoneNumberSetsPresentation = ({ telephoneNumberList, vm, intl }: ITelephoneNumberSetProps) => {
    const { telephoneNumberSets, fetchError } = telephoneNumberList;

    const thead: ITheadSort<TTelephoneNumberSetsRequestSortKeys>[] = [
        {
            title: intl.formatMessage({
                id: `oe.telephone.name`,
                defaultMessage: 'Number Set Name',
                description: 'Label text',
            }),
            sortField: 'name',
            width: '30%',
        },
        {
            title: intl.formatMessage({
                id: `oe.telephone.provider`,
                defaultMessage: 'Provider',
                description: 'Label text',
            }),
            sortField: 'provider',
            width: '30%',
        },
        {
            title: intl.formatMessage({
                id: `oe.telephone.telephoneNumebr`,
                defaultMessage: 'Telephone Numbers',
                description: 'Label text',
            }),
            sortField: 'telephoneNumbersCount',
            width: '30%',
        },
        {
            customNode: (
                <div className={'search-input-container'}>
                    <SearchInput
                        resetSearch={() => telephoneNumberList.setSearchQuery('', true)}
                        value={telephoneNumberList.searchQuery}
                        onChange={e => telephoneNumberList.setSearchQuery(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                telephoneNumberList.executeSearch();
                            }
                        }}
                        withResetSearchButton
                    />
                </div>
            ),
            width: '160px',
        },
    ];

    const showTelephoneNumeberSetsList = !!(!fetchError &&
        telephoneNumberSets && telephoneNumberSets.length
    );
    const showFetchingTelephoneNumbers = !telephoneNumberSets && !fetchError;
    const showNoNumberSets = !fetchError && telephoneNumberSets && !telephoneNumberSets.length;
    const COL_SPAN = thead.length;

    return (
        <div className="telephone-numbersets-list">
            <section className="resources-tabs horizontal-row horizontal-row--with-bottom-margin">
                <ResourcesNavigation />
                <div className="horizontal-row__align-to-row-end">
                    <DefaultButton
                        text={
                            <FormattedMessage
                                id="oe.telephone.newNumberSet"
                                defaultMessage="+ New Number Set"
                                description="Button text"
                            />
                        }
                        modifier={['fat', 'confirm-safe']}
                        onClick={() => vm.showTelephoneNumberSetForm('New Number Set')}
                    />
                </div>
            </section>
            <section className="telephone-numbersets-list fixed-list-layout">
                <ListLayout
                    sortOptions={{
                        theads: thead,
                        sortRows: telephoneNumberList.sortRows,
                        sortField: telephoneNumberList.sortField,
                        sortOrder: telephoneNumberList.sortOrder,
                    }}
                >
                    {showTelephoneNumeberSetsList &&
                        telephoneNumberSets.map(x => (
                            <>
                                <TelephoneNumberSet {...x} key={x.id}
                                    isSelected={vm.isNumberSetEditFormShown(x.id)}
                                    editNumberSet={vm.editNumberSet}
                                />
                            </>
                        ))}
                    {showFetchingTelephoneNumbers && (
                        <tr>
                            <td className="table-loading" colSpan={COL_SPAN}>
                                <FormattedMessage
                                    id="oe.common.loading"
                                    defaultMessage="Loading{suffix}"
                                    description="Loading placeholder"
                                    values={{
                                        suffix: '...',
                                    }}
                                />
                            </td>
                        </tr>
                    )}
                    {fetchError && <tr>
                        <td className='table-loading' colSpan={12}>{fetchError}</td>
                    </tr>}
                    {showNoNumberSets && (
                        <tr>
                            <td className="table-loading" colSpan={COL_SPAN}>
                                <NoDataFound
                                    imageDescriptionText={
                                        <FormattedMessage
                                            id="oe.telephone.noNumberSetsFound"
                                            defaultMessage="No Telephone Number Sets found."
                                            description="Label text"
                                        />
                                    }
                                />
                            </td>
                        </tr>
                    )}
                </ListLayout>
            </section>
            <Paging pagingVM={telephoneNumberList.pagingVM} />
        </div>
    );
};

const TelephoneNumberSetPage = injectIntl(
    connectZoomVM<ITelephoneNumberSetProps>(
        TelephoneNumberSetsPresentation,
        zoomVM => {
            const telephoneNumberSets = zoomVM
                .getTelephoneNumberSetsVM()
                .getTelephoneNumberSets();
            const pagingVM = zoomVM.getTelephoneNumberSetsVM().getPagingVM();
            const telephoneNumberSetsVM = zoomVM.getTelephoneNumberSetsVM();

            return {
                telephoneNumberList: {
                    pagingVM: pagingVM,
                    telephoneNumberSets: telephoneNumberSets,
                    telephoneNumberSetsLength:
                        telephoneNumberSets && telephoneNumberSets.length,
                    pageNumber: pagingVM.getCurrentPage(),
                    pageSize: pagingVM.getPageSize(),
                    sortRows: telephoneNumberSetsVM.sortTableRows.bind(
                        telephoneNumberSetsVM
                    ),
                    sortField: telephoneNumberSetsVM.getSortField(),
                    sortOrder: telephoneNumberSetsVM.getSortOrder(),
                    searchQuery: telephoneNumberSetsVM.getSearchQuery(),
                    setSearchQuery: telephoneNumberSetsVM.setSearchQuery,
                    executeSearch: telephoneNumberSetsVM.executeSearch,
                    fetchError: telephoneNumberSetsVM.getFetchError(),
                },
                vm: telephoneNumberSetsVM,
            };
        }
    )
);

export { TelephoneNumberSetPage, TelephoneNumberSet };
